import { TitleWithNumber, useDialog, SidePane } from "@endya-conformite/ui-kit";
import { GatewayAssujettiApi } from "@src/api/assujetti.api";
import {
  AssujettiPaneContent,
  AssujettiPaneContentPage,
} from "@src/assujettis/AssujettiPaneContent/AssujettiPaneContent";
import { useAssujettiPane } from "@src/assujettis/AssujettiPaneContent/useAssujettiPane";
import { useImportAssujetti } from "@src/assujettis/ImportExport/Import/useImportAssujetti";
import { useAssujetisAvancement } from "@src/assujettis/useAssujettiAvancement";
import { useAssujetti } from "@src/store/store.assujetti";
import { Assujetti } from "@src/store/store.definition";
import { useMillesime } from "@src/store/store.millesime";
import { useCallback, useMemo } from "react";
import {
  ParcoursConformiteStepComponentProps,
  ParcoursConformiteStepEnum,
  useParcoursConformite,
} from "../../ParcoursConformite";
import { ParcoursConformiteLoaderStep } from "../../ParcoursConformiteLoaderStep";
import { ParcoursConformiteTemplate } from "../../ParcoursConformiteTemplate";
import { ConformiteAssujettisList } from "./ConformiteAssujettisList";
import { ConformiteImportAssujettis } from "./ConformiteImportAssujettis";

export function ConformiteAssujettisStep({
  siren,
  changeStep,
  currentStep,
  addData,
}: ParcoursConformiteStepComponentProps) {
  const { entreprise } = useParcoursConformite();
  const { assujettisBySocietes, assujettis, removeEntries, setEntry } =
    useAssujetti();
  const { parseAssujettisFile } = useImportAssujetti();
  const [assujettiPaneState, setAssujettiPaneState] = useAssujettiPane();
  const { getAssujetisAvancement } = useAssujetisAvancement();
  const { selected: millesime } = useMillesime();
  const { openConfirmDialog } = useDialog();

  const assujettisOfEntreprise = useMemo(
    () => assujettisBySocietes[siren] ?? [],
    [assujettisBySocietes, siren]
  );

  const handleUpdatePane = useCallback(
    (id: string, pane: AssujettiPaneContentPage) => {
      if (!assujettis || !assujettis[id]) return;

      setAssujettiPaneState((prevState) => ({
        assujetti: assujettis[id],
        initialPage: pane,
        isOpen: true,
        paneSession:
          prevState.assujetti?.id === id && pane === prevState.initialPage
            ? prevState.paneSession
            : prevState.paneSession + 1,
        defaultSiren: siren,
      }));
    },
    [assujettis, setAssujettiPaneState, siren]
  );

  const handleOpenAddAssujettiPane = useCallback(
    () =>
      setAssujettiPaneState((prevState) => ({
        assujetti: null,
        initialPage: "information-personne",
        isOpen: true,
        paneSession:
          prevState.assujetti === null &&
          prevState.initialPage === "information-personne"
            ? prevState.paneSession
            : prevState.paneSession + 1,
        defaultSiren: siren,
      })),
    [siren, setAssujettiPaneState]
  );

  const handleDeleteMultipleAssujettis = useCallback(
    async (selectedRows: Assujetti[]) => {
      if (!millesime) return;
      const shouldContinue = await openConfirmDialog({
        title: `Êtes-vous sur(e) de vouloir supprimer ${
          selectedRows.length > 1
            ? "ces personnes assujetties"
            : "cette personne assujettie"
        } ?`,
        message: "Cette action est irréversible.",
        confirmText: "Confirmer",
        cancelText: "Annuler",
      });

      if (!shouldContinue) return;

      await GatewayAssujettiApi.deleteMultiple(
        selectedRows.map((r) => r.id),
        millesime
      );
      removeEntries(
        selectedRows.map((r) => r.id),
        millesime
      );
    },
    [millesime, openConfirmDialog, removeEntries]
  );

  const isConforme = useMemo(
    () =>
      assujettisOfEntreprise.length > 0 &&
      assujettisOfEntreprise.every(
        (assujetti) => getAssujetisAvancement(assujetti) === 100
      ),
    [assujettisOfEntreprise, getAssujetisAvancement]
  );

  if (!millesime || !entreprise)
    return (
      <ParcoursConformiteLoaderStep
        currentStep={currentStep}
        changeStep={changeStep}
        title="Personnes assujetties"
        size="large"
      />
    );
  return (
    <>
      <ParcoursConformiteTemplate
        currentStep={currentStep}
        changeStep={changeStep}
        isConforme={isConforme}
        title={
          <TitleWithNumber
            title="Personnes assujetties"
            number={
              assujettisOfEntreprise.length > 0
                ? assujettisOfEntreprise.length
                : undefined
            }
          />
        }
        size="large"
      >
        {assujettisOfEntreprise.length === 0 && (
          <ConformiteImportAssujettis
            onOpenAddAssujetti={handleOpenAddAssujettiPane}
            onImportAssujettis={(file: File) => {
              parseAssujettisFile(
                file,
                ({ assujettisInError, assujettisToAdd }) => {
                  addData({
                    assujettis: {
                      errors: assujettisInError,
                      success: assujettisToAdd,
                    },
                  });
                  changeStep(
                    ParcoursConformiteStepEnum.IMPORT_ASSUJETTIS_DASHBOARD
                  );
                },
                siren
              );
            }}
          />
        )}
        {assujettisOfEntreprise.length > 0 && (
          <ConformiteAssujettisList
            onUpdatePane={handleUpdatePane}
            onDeleteMultipleAssujettis={handleDeleteMultipleAssujettis}
            onOpenAddAssujettiPane={handleOpenAddAssujettiPane}
            siren={siren}
            assujettisWithAvancement={assujettisOfEntreprise}
          />
        )}
      </ParcoursConformiteTemplate>

      {millesime && (
        <SidePane
          isOpen={assujettiPaneState.isOpen}
          setIsOpen={(nextIsOpen) =>
            setAssujettiPaneState((prevSate) => ({
              ...prevSate,
              isOpen: nextIsOpen,
            }))
          }
        >
          {(closePane) => (
            <AssujettiPaneContent
              key={assujettiPaneState.paneSession}
              millesime={millesime}
              setEntry={(assujetti) => {
                setEntry(assujetti);
                setAssujettiPaneState((prevState) => ({
                  ...prevState,
                  assujetti,
                }));
              }}
              closePane={closePane}
              assujetti={assujettiPaneState.assujetti}
              initialPage={assujettiPaneState.initialPage}
              defaultSiren={assujettiPaneState.defaultSiren}
            />
          )}
        </SidePane>
      )}
    </>
  );
}
